import { Fragment, useEffect, useMemo, useState } from "react";

import { Dialog } from "primereact/dialog";

import { useDataTable } from "../../../shared/hooks/DataTable";
import { IamColumn } from "../../../shared/hooks/DataTableBasic";
import { FormatDisplay } from "../../../shared/format";
import { PaymentLogService } from "../../lich-su-truyen-nhan/PaymentLogService";
import {
  formatPaymentAmountVnd,
  formatPaymentDateTime,
  renderInvoicePaymentStatusBadge,
} from "../../lich-su-truyen-nhan/lich-su-thanh-toan/paymentLogDisplay";
import type { DataSourceValue } from "../../lich-su-truyen-nhan/lich-su-thanh-toan/constants";
import { useTranslation } from "../../../shared/language";

const PAYMENT_HISTORY_CONFIG: Record<
  DataSourceValue,
  {
    headerKey: string;
    emptyMessageKey: string;
    buildListParams: (id: string) => Record<string, unknown>;
    buildSubtitle: (item: PaymentHistorySelectedItem, t: (key: string) => string) => string;
  }
> = {
  invoice: {
    headerKey: "common.invoicePaymentHistory",
    emptyMessageKey: "common.invoiceNoPayment",
    buildListParams: (id) => ({
      resourceId: id,
      sourceType: ["invoice"],
      sorting: "creationTime",
      sortType: "DESCEND",
    }),
    buildSubtitle: (item, t) => {
      if (!item.invoiceSerial || !item.invoiceNumber) {
        return "";
      }

      return `${t("common.symbol")} ${item.invoiceSerial}, ${t("common.invoiceNo")} ${item.invoiceNumber}${
        item.invoiceDate ? `, ${t("common.invoiceDate")} ${FormatDisplay.date(item.invoiceDate)}` : ""
      }`;
    },
  },
  fuel_log: {
    headerKey: "common.qrPaymentHistory",
    emptyMessageKey: "common.qrNoPayment",
    buildListParams: (id) => ({
      resourceId: id,
      sourceType: ["fuel_log"],
      sorting: "creationTime",
      sortType: "DESCEND",
    }),
    buildSubtitle: (item, t) => {
      const parts: string[] = [];

      if (item.deviceCode) {
        parts.push(`${t("common.pump")} ${item.deviceCode}`);
      }
      if (item.storeCode) {
        parts.push(`${t("common.store")} ${item.storeCode}`);
      }
      if (item.timeStamp) {
        parts.push(`${t("common.time")} ${FormatDisplay.dateTime(item.timeStamp)}`);
      }

      return parts.join(", ");
    },
  },
};

export type PaymentHistorySelectedItem = {
  id?: string;
  invoiceSerial?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  deviceCode?: string;
  storeCode?: string;
  timeStamp?: string;
};

type DialogInvoicePaymentHistoryProps = {
  display: boolean;
  setDisplay: (visible: boolean) => void;
  sourceType?: DataSourceValue;
  selectedItem?: PaymentHistorySelectedItem | null;
};

export const DialogInvoicePaymentHistory = ({
  display,
  setDisplay,
  sourceType = "invoice",
  selectedItem,
}: DialogInvoicePaymentHistoryProps) => {
  const { t } = useTranslation();
  const config = PAYMENT_HISTORY_CONFIG[sourceType];
  const [requiredParams, setRequiredParams] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  useEffect(() => {
    if (display && selectedItem?.id) {
      setRequiredParams(config.buildListParams(selectedItem.id));
      return;
    }

    setRequiredParams(null);
    setIsEmpty(false);
  }, [config, display, selectedItem?.id]);

  const columns = useMemo((): IamColumn[] => [
      {
        field: "transactionId",
        header: t("common.transactionId"),
        width: 180,
      },
      {
        field: "paidAmount",
        header: t("common.paidAmount"),
        width: 170,
        dataType: "custom",
        style: { justifyContent: "end" },
        customCell(rowData: { paidAmount?: number }) {
          return (
            <span className="block text-right w-full">
              {formatPaymentAmountVnd(rowData.paidAmount)}
            </span>
          );
        },
      },
      {
        field: "paymentTime",
        header: t("common.paymentTime"),
        width: 180,
        dataType: "custom",
        customCell(rowData: { paymentTime?: string }) {
          return formatPaymentDateTime(rowData.paymentTime);
        },
      },
      {
        field: "paymentStatus",
        header: t("common.paymentStatus"),
        width: 170,
        dataType: "custom",
        style: { justifyContent: "center" },
        customCell(rowData: { paymentStatus?: number }) {
          return renderInvoicePaymentStatusBadge(rowData.paymentStatus);
        },
      },
      {
        field: "paymentContent",
        header: t("common.content"),
        minWidth: 220,
      },
    ], [t]);

  const { render: renderDataTable } = useDataTable({
    columns,
    rows: 20,
    heightCalc: 220,
    hideRowsPerPageDropdown: true,
    hideCurrentPageReport: false,
    requiredParams,
    getList: (params) =>
      PaymentLogService.getList(params).then((data) => {
        setIsEmpty((data?.totalCount ?? 0) === 0);
        return data;
      }),
  });

  const subtitle = selectedItem ? config.buildSubtitle(selectedItem, t) : "";

  return (
    <Dialog
      header={<span className="text-xl font-bold">{t(config.headerKey)}</span>}
      visible={display}
      style={{ width: "80vw", maxWidth: "1200px" }}
      position="top"
      draggable={false}
      onHide={() => setDisplay(false)}
      className="dialog-invoice-payment-history"
    >
      <Fragment>
        {subtitle ? (
          <p className="text-center m-0 mb-3">
            <b style={{ fontSize: "1.25rem", fontWeight: 700 }}>{subtitle}</b>
          </p>
        ) : null}
        {isEmpty && requiredParams ? (
          <p className="text-center text-color-secondary m-0 mb-3">
            {t(config.emptyMessageKey)}
          </p>
        ) : null}
        <div className="dialog-invoice-payment-history-table">
          {renderDataTable()}
        </div>
      </Fragment>
    </Dialog>
  );
};
